import React, { forwardRef } from 'react';
import { LuCamera as FiCamera, LuMic as FiMic } from "react-icons/lu";
import { LuSendHorizontal } from 'react-icons/lu';
import { ReplyQuotedMessage } from './ReplyQuotedMessage';

const MessageInput = forwardRef(({
    value,
    onChange,
    onKeyDown,
    onBlur,
    placeholder,
    isUploading,
    onCameraClick,
    onSend,
    onRecordStart,
    isRecording,
    replyingTo,
    onClearReply,
    currentUserId,
    senderName,
    editingMessage,
    autoResize,
    disabled = false,
    showSendButton = true,
    showCamera = true,
    showMic = true,
    className = ""
}, ref) => {
    return (
        <div className="w-full">
            {replyingTo && (
                <div className="mb-2">
                    <ReplyQuotedMessage
                        replyTo={replyingTo}
                        onClear={onClearReply}
                        currentUserId={currentUserId}
                        senderName={senderName}
                    />
                </div>
            )}

            <div className="flex items-center space-x-2 md:space-x-4 md:max-w-[100%] md:ml-4">
                <textarea
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    rows={1}
                    disabled={disabled}
                    className={`
                        flex-1 w-full rounded-2xl px-4 py-2 outline-none resize-none overflow-hidden
                        placeholder:text-gray-500 dark:placeholder:text-gray-400
                        bg-gray-100 dark:bg-[#2A2A2A]
                        text-black dark:text-white
                        border border-gray-200 dark:border-0
                        max-h-[7rem] transition-all duration-100 ease-in-out
                        ${className}
                    `}
                />

                {showCamera && (
                    <div className="relative">
                        <button
                            onClick={onCameraClick}
                            className="p-2 rounded-full flex-shrink-0 hover:bg-gray-200 dark:hover:bg-gray-500/30 text-gray-800 dark:text-white"
                            disabled={isUploading || disabled}
                        >
                            {!isUploading ? (
                                <FiCamera className="w-5 h-5" />
                            ) : (
                                <div className="w-5 h-5 border-2 border-gray-300 border-t-[#327691] rounded-full animate-spin"></div>
                            )}
                        </button>
                    </div>
                )}

                {showSendButton && (value?.trim() !== "" || replyingTo) ? (
                    <button
                        onClick={onSend}
                        className="ml-2 p-2 rounded-full flex-shrink-0 hover:bg-gray-200 dark:hover:bg-gray-500/30 text-gray-800 dark:text-white"
                        title="Send Message"
                        disabled={disabled}
                    >
                        <LuSendHorizontal className="w-5 h-5" />
                    </button>
                ) : showMic ? (
                    <button
                        onClick={onRecordStart}
                        className="p-2 rounded-full flex-shrink-0 hover:bg-gray-200 dark:hover:bg-gray-500/30 text-gray-800 dark:text-white"
                        disabled={isRecording || disabled}
                        title="Record Voice Message"
                    >
                        {!isRecording ? (
                            <FiMic className="w-5 h-5" />
                        ) : (
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-[#327691] rounded-full animate-spin"></div>
                        )}
                    </button>
                ) : null}
            </div>
        </div>
    );
});

MessageInput.displayName = 'MessageInput';

export default MessageInput;